import { deviceLocal } from '@/privacy/deviceStorage';
/**
 * F-0585: the pinned sidebar's widths and the pure reading of them, apart from the component so a
 * spec can import them without the app's i18n (whose catalogue reads Vite's build constants, which a
 * Playwright process does not define; `PinnedSidebar.tsx` re-exports them).
 */
/** The width the pinned sidebar had before it could be resized (`w-64`), and has until one is chosen. */
export const PINNED_SIDEBAR_DEFAULT_WIDTH = 256;
/**
 * F-0585 (PSW-7): measured, not guessed. At 1280 and at 1024 (Geist is bundled, so every platform
 * draws the same), the narrowest width at which every line of the sidebar is still usable is 232:
 * below it the Work areas heading is cut ("WORK A…"); below 212 the account box's icons, Theme and
 * Sign out wrap to a second line. Each namespace keeps its first word whole beside its controls at
 * every width from 160. 4 px are kept above the measured 232 for rounding at other pixel ratios
 * (`docs/concept-work-area-views-in-the-sidebar.md`, *F-0585*).
 */
export const PINNED_SIDEBAR_MIN_WIDTH = 236;
/** The widest the sidebar grows, whatever the window: past it, a line is longer than it is read. */
export const PINNED_SIDEBAR_MAX_WIDTH = 480;
/** The share of the window the sidebar may take at most, so the page beside it keeps the rest. */
export const PINNED_SIDEBAR_MAX_VIEWPORT_RATIO = 0.4;
export const PINNED_SIDEBAR_KEYBOARD_STEP = 16;
/**
 * F-0585 (PSW-5). Per device, not a synced preference: a pixel width only means something on the
 * screen it was chosen on (the reasoning I-0629 gave the Activity split).
 */
export const PINNED_SIDEBAR_WIDTH_STORAGE_KEY = 'featit:pinnedSidebarWidth';

export function pinnedSidebarWidthBounds(viewportWidth: number): { min: number; max: number } {
  const max = Math.max(
    PINNED_SIDEBAR_MIN_WIDTH,
    Math.min(
      PINNED_SIDEBAR_MAX_WIDTH,
      Math.round(viewportWidth * PINNED_SIDEBAR_MAX_VIEWPORT_RATIO),
    ),
  );
  return { min: PINNED_SIDEBAR_MIN_WIDTH, max };
}

export function clampPinnedSidebarWidth(width: number, viewportWidth: number): number {
  const bounds = pinnedSidebarWidthBounds(viewportWidth);
  return Math.min(bounds.max, Math.max(bounds.min, Math.round(width)));
}

/**
 * The width a key asks for from `width`, or `null` for a key the handle does not take. Shift makes
 * an arrow's step four times larger; Enter is the double-click's reset.
 */
export function pinnedSidebarWidthForKey(
  key: string,
  shiftKey: boolean,
  width: number,
  viewportWidth: number,
): number | null {
  const step = shiftKey ? PINNED_SIDEBAR_KEYBOARD_STEP * 4 : PINNED_SIDEBAR_KEYBOARD_STEP;
  const bounds = pinnedSidebarWidthBounds(viewportWidth);
  if (key === 'ArrowRight') return clampPinnedSidebarWidth(width + step, viewportWidth);
  if (key === 'ArrowLeft') return clampPinnedSidebarWidth(width - step, viewportWidth);
  if (key === 'Home') return bounds.min;
  if (key === 'End') return bounds.max;
  if (key === 'Enter') return clampPinnedSidebarWidth(PINNED_SIDEBAR_DEFAULT_WIDTH, viewportWidth);
  return null;
}

/**
 * The width the person chose last time, or `null` when there is none to read — never chosen,
 * unreadable, or storage that throws (a private window, blocked site data).
 */
export function storedPinnedSidebarWidth(): number | null {
  try {
    const raw = deviceLocal.getItem(PINNED_SIDEBAR_WIDTH_STORAGE_KEY);
    if (raw === null) return null;
    const stored = Number(raw);
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  } catch {
    return null;
  }
}

export function storePinnedSidebarWidth(width: number): void {
  try {
    deviceLocal.setItem(PINNED_SIDEBAR_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // Blocked storage costs the next visit its remembered width, never this one its sidebar.
  }
}
